import * as React from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { styled } from '@mui/material/styles';
import {
    OperationType,
    ValueType,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import { useTranslation } from 'react-i18next';

type Props<T extends OperationType | ValueType> = {
    optionSelected: T;
    setOption: (value: T) => void;
    option1: T;
    option2: T;
    option3?: T;
};

const StyToggleButton = styled(ToggleButton, { name: 'StyToggleButton' })(
    ({ theme }) => ({
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
        '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.lightGrey,
        },
        '&.MuiToggleButton-root': {
            '&:first-child': {
                borderTopLeftRadius: '12px',
                borderBottomLeftRadius: '12px',
            },
            '&:last-child': {
                borderTopRightRadius: '12px',
                borderBottomRightRadius: '12px',
            },
        },
        '&.MuiButtonBase-root': {
            textTransform: 'unset !important',
            fontWeight: 600,
        },
        fontSize: '11px',
    }),
);

export default function ToggleHypothesisConditions<
    T extends OperationType | ValueType,
>({
    optionSelected,
    setOption,
    option1,
    option2,
    option3 = undefined,
}: Props<T>): React.ReactElement {
    const { t } = useTranslation();
    const handleChange = React.useCallback(
        (event: React.MouseEvent<HTMLElement>, newOption: T) => {
            if (newOption) setOption(newOption);
        },
        [setOption],
    );
    const toggleButtonWidth = option3 ? '33%' : '50%';

    return (
        <ToggleButtonGroup
            color="primary"
            value={optionSelected}
            size="small"
            exclusive
            onChange={handleChange}
            sx={{ height: '2rem', width: '100%', margin: '0.2rem' }}
        >
            <StyToggleButton value={option1} sx={{ width: toggleButtonWidth }}>
                <>
                    {t(
                        `humanResources.simulation.freeHypothesis.ifse.${option1}`,
                    )}
                </>
            </StyToggleButton>
            <StyToggleButton value={option2} sx={{ width: toggleButtonWidth }}>
                <>
                    {t(
                        `humanResources.simulation.freeHypothesis.ifse.${option2}`,
                    )}
                </>
            </StyToggleButton>
            {option3 && (
                <StyToggleButton
                    value={option3}
                    sx={{ width: toggleButtonWidth }}
                >
                    <>
                        {t(
                            `humanResources.simulation.freeHypothesis.ifse.${option3}`,
                        )}
                    </>
                </StyToggleButton>
            )}
        </ToggleButtonGroup>
    );
}
